'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  CheckCircle2,
  DollarSign,
  Download,
  FileText,
  Network,
  Package,
  Search,
  Ship,
  TrendingUp,
} from 'lucide-react';

import {
  DEFAULT_CURRENCY,
  formatMoney,
  getCurrencySymbol,
} from '@kit/shared/currency';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';

const baseUrl = appConfig.url;
const siteName = appConfig.name;
const calculatorUrl = `${baseUrl}/tools/landed-cost-calculator`;
const toolsUrl = `${baseUrl}/tools`;

type FormData = {
  productCost: string;
  freightCost: string;
  dutyRate: string;
  quantity: string;
};

type CalculationResult = {
  totalProductCost: number;
  freightCost: number;
  dutyRate: number;
  dutyAmount: number;
  totalLandedCost: number;
  landedCostPerUnit: number;
  freightPerUnit: number;
};

const faqItems = [
  {
    value: 'how-to-calculate',
    question: 'How do you calculate landed cost?',
    answer:
      'Landed cost per unit = (product × quantity + freight + duty) ÷ quantity. This calculator applies the duty rate to product value only, then adds the freight total you enter. Example: 500 units at $12, $840 freight, 7.5% duty → $6,000 product + $450 duty + $840 freight = $7,290, or $14.58 per unit. Fold insurance, brokerage, or port fees into freight if they are billed separately.',
  },
  {
    value: 'whats-included',
    question: 'What is included in landed cost?',
    answer:
      'Everything that makes one unit sellable: supplier price, inbound freight, duty and tariffs, broker and entry fees, insurance, currency conversion, and handling. Referral fees, ads, and payment processing are selling costs, not landed cost. This tool takes the three numbers you usually have before a PO: unit cost, freight, and duty rate.',
  },
  {
    value: 'worked-example',
    question: 'What does a typical import look like in this calculator?',
    answer:
      'A $12 factory price on 500 units looks cheap until freight and duty land. At $840 ocean freight and 7.5% duty, the shipment is $7,290 and each unit is $14.58 — 21.5% above the invoice. That is the number to compare against your selling price, not $12. Same freight on 100 units would add $8.40 per unit instead of $1.68.',
  },
  {
    value: 'free',
    question: 'Is this landed cost calculator free?',
    answer:
      'Yes. It runs in your browser with no signup, no usage limit, and no credit card. Export any calculation to CSV.',
  },
  {
    value: 'per-unit',
    question: 'Why does landed cost per unit matter more than total cost?',
    answer:
      'Per-unit landed cost is what you price against. The same $840 freight bill is $1.68 per unit on 500 pieces and $8.40 on 100. That is why a small PO can look profitable on the supplier quote and lose money after it lands.',
  },
  {
    value: 'duty-basis',
    question: 'Is duty calculated on product only, or on CIF value?',
    answer:
      'This page applies the duty rate to product value only, which matches a typical FOB commercial invoice. If your entry is CIF, add freight (and insurance) into the product side or raise the duty rate to approximate the broader base. Confirm the live HTS rate and fee stack in the import duty calculator before you treat the estimate as an entry.',
  },
  {
    value: 'vat',
    question: 'Does this calculator include VAT?',
    answer:
      'No. It models inbound landed cost: product, freight, and duty. VAT or GST can sit on top for destination tax planning and is not in the per-unit figure here.',
  },
  {
    value: 'fob-cif',
    question: 'What is the difference between FOB and CIF?',
    answer:
      'FOB means the supplier is responsible until the goods are loaded at origin, so you usually pay freight separately. CIF includes cost, insurance, and freight to the destination port. Your landed cost changes because the quote already contains different lines — do not add freight twice.',
  },
  {
    value: 'hs-code',
    question: "How do I find my product's HS code?",
    answer:
      'Ask your broker or supplier, then confirm the code in the destination tariff. A one-digit classification miss can move duty enough to erase the margin. Use the import duty calculator for a live USITC HTS lookup once you have a candidate code.',
  },
] as const;

const impactCards = [
  {
    title: 'Price off the real unit, not the invoice',
    description:
      'A $12 factory quote that lands at $14.58 is a different product. If you advertised from $12, the freight invoice is where the margin went.',
    icon: TrendingUp,
  },
  {
    title: 'Compare suppliers on the same basis',
    description:
      'A cheaper EXW quote can lose to a dearer FOB quote once pickup, freight, and duty are in the same column.',
    icon: Search,
  },
  {
    title: 'See what a small PO actually costs',
    description:
      'Fixed freight and entry fees do not shrink with quantity. 100 units can carry five times the freight per unit of a 500-unit lot.',
    icon: Package,
  },
  {
    title: 'Leave room before you commit',
    description:
      'Duty, fuel, and destination charges move after the PO. Knowing cost per unit early is what lets you walk away or reprice.',
    icon: DollarSign,
  },
] as const;

/**
 * Shown until there is enough input to calculate. A literal "$0.00" both
 * claimed a result that had not been calculated and hard-coded a dollar sign
 * the currency picker is allowed to change.
 */
const EMPTY_VALUE = '—';

function parseNumber(value: string) {
  const numeric = Number.parseFloat(value.replace(/,/g, ''));
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

function calculateResults(formData: FormData): CalculationResult | null {
  const productCost = parseNumber(formData.productCost);
  const freightCost = parseNumber(formData.freightCost);
  const dutyRate = parseNumber(formData.dutyRate);
  const quantity = parseNumber(formData.quantity);

  if (quantity <= 0) {
    return null;
  }

  const totalProductCost = Math.round(productCost * quantity * 100) / 100;
  const dutyAmount =
    Math.round(totalProductCost * (dutyRate / 100) * 100) / 100;
  const totalLandedCost =
    Math.round((totalProductCost + freightCost + dutyAmount) * 100) / 100;
  const landedCostPerUnit =
    Math.round((totalLandedCost / quantity) * 100) / 100;
  const freightPerUnit = Math.round((freightCost / quantity) * 100) / 100;

  return {
    totalProductCost,
    freightCost,
    dutyRate,
    dutyAmount,
    totalLandedCost,
    landedCostPerUnit,
    freightPerUnit,
  };
}

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Landed Cost Calculator: Cost Per Unit After Freight & Duty',
    alternateName: 'Landed Cost Calculator',
    description:
      'Calculate landed cost per unit from product cost, freight, and duty rate. Export the shipment total to CSV.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    featureList: [
      'Calculate landed cost per unit',
      'Estimate total landed cost for a shipment',
      'Estimate customs duty from a duty rate',
      'Break out freight cost per unit',
      'Export the calculation to CSV',
    ],
    creator: {
      '@type': 'Organization',
      name: siteName,
      url: baseUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Landed Cost Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

export default function LandedCostCalculatorPage() {
  // Empty, not a worked example. Pre-filled values rendered a confident
  // "$57.75 per unit" to a visitor who had typed nothing, which reads as the
  // tool's answer to their shipment rather than as sample data.
  const [formData, setFormData] = useState<FormData>({
    productCost: '',
    freightCost: '',
    dutyRate: '',
    quantity: '',
  });
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const formatCurrency = (value: number) => formatMoney({ currency, value });
  const currencySymbol = getCurrencySymbol(currency);

  const startFreePath = pathsConfig.auth.startFree;
  const results = calculateResults(formData);

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setFormData((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function downloadReport() {
    if (!results) {
      return;
    }

    const rows = [
      ['Metric', 'Value'],
      ['Currency', currency],
      ['Product unit cost', formData.productCost],
      ['Freight cost', formData.freightCost],
      ['Duty rate', `${formData.dutyRate}%`],
      ['Total units', formData.quantity],
      ['Total product cost', results.totalProductCost.toFixed(2)],
      ['Total duties', results.dutyAmount.toFixed(2)],
      ['Freight per unit', results.freightPerUnit.toFixed(2)],
      ['Total landed cost', results.totalLandedCost.toFixed(2)],
      ['Landed cost per unit', results.landedCostPerUnit.toFixed(2)],
    ];

    const csv = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `landed-cost-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <StructuredData />

      <div className="bg-muted text-foreground flex-1">
        <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="font-heading text-foreground text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                <span className="text-primary">Landed Cost Calculator</span>
                : Cost Per Unit After Freight and Duty
              </h1>
              <p className="text-muted-foreground mx-auto mt-6 max-w-3xl text-lg leading-8">
                Free tool for FBA, Shopify, and importers. See what one unit
                actually costs after product, freight, and duty, before you
                place the PO. A $12 factory price on 500 units with $840 freight
                and 7.5% duty lands at $14.58, not $12.
              </p>
            </div>

            <Card className="border-border/80 bg-card mt-12 rounded-[10px] border shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)]">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                <div className="grid gap-8 lg:grid-cols-5 lg:items-start">
                  <div className="lg:col-span-3">
                    <div className="mb-6 flex items-center gap-2">
                      <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-md">
                        <FileText className="h-4 w-4" />
                      </div>
                      <h2 className="text-foreground text-xl font-bold">
                        Product, freight, and duty
                      </h2>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <CurrencyPicker onChange={setCurrency} value={currency} />

                      <div className="space-y-2">
                        <Label
                          htmlFor="productCost"
                          className="text-muted-foreground text-sm font-semibold"
                        >
                          Product unit cost ({currency})
                        </Label>
                        <div className="relative">
                          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2">
                            {currencySymbol}
                          </span>
                          <Input
                            id="productCost"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.productCost}
                            onChange={(e) =>
                              updateField('productCost', e.target.value)
                            }
                            className="border-border bg-muted focus-visible:ring-primary/20 h-12 rounded-xl pl-8 shadow-none focus-visible:ring-2"
                            placeholder="e.g. 12.00"
                          />
                        </div>
                        <p className="text-muted-foreground text-xs leading-5">
                          Supplier invoice price per unit.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="freightCost"
                          className="text-muted-foreground text-sm font-semibold"
                        >
                          Freight cost ({currency})
                        </Label>
                        <div className="relative">
                          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2">
                            {currencySymbol}
                          </span>
                          <Input
                            id="freightCost"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.freightCost}
                            onChange={(e) =>
                              updateField('freightCost', e.target.value)
                            }
                            className="border-border bg-muted focus-visible:ring-primary/20 h-12 rounded-xl pl-8 shadow-none focus-visible:ring-2"
                            placeholder="e.g. 840.00"
                          />
                        </div>
                        <p className="text-muted-foreground text-xs leading-5">
                          Shipment total. Fold in insurance and brokerage if
                          they are billed separately.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="dutyRate"
                          className="text-muted-foreground text-sm font-semibold"
                        >
                          Duty rate (%)
                        </Label>
                        <div className="relative">
                          <Input
                            id="dutyRate"
                            type="number"
                            min="0"
                            step="0.1"
                            value={formData.dutyRate}
                            onChange={(e) =>
                              updateField('dutyRate', e.target.value)
                            }
                            className="border-border bg-muted focus-visible:ring-primary/20 h-12 rounded-xl pr-9 shadow-none focus-visible:ring-2"
                            placeholder="e.g. 7.5"
                          />
                          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2">
                            %
                          </span>
                        </div>
                        <p className="text-muted-foreground text-xs leading-5">
                          Applied to product value. Look up a live US HTS rate
                          in the{' '}
                          <Link
                            href="/tools/import-duty-calculator"
                            className="text-primary font-semibold hover:underline"
                          >
                            import duty calculator
                          </Link>
                          .
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="quantity"
                          className="text-muted-foreground text-sm font-semibold"
                        >
                          Total units
                        </Label>
                        <Input
                          id="quantity"
                          type="number"
                          min="1"
                          step="1"
                          value={formData.quantity}
                          onChange={(e) =>
                            updateField('quantity', e.target.value)
                          }
                          className="border-border bg-muted focus-visible:ring-primary/20 h-12 rounded-xl shadow-none focus-visible:ring-2"
                          placeholder="e.g. 500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2 lg:flex lg:justify-end">
                    <div className="w-full rounded-2xl bg-[#eaf2f8] p-6 text-[#17324d] shadow-lg">
                      <p className="text-xs font-bold tracking-[0.16em] text-[#315572] uppercase">
                        Real-Time Result
                      </p>
                      <div className="mt-3 flex items-end gap-2">
                        <span className="text-4xl font-extrabold tracking-tight">
                          {results
                            ? formatCurrency(results.landedCostPerUnit)
                            : EMPTY_VALUE}
                        </span>
                        <span className="pb-1 text-sm text-[#315572]">
                          per unit
                        </span>
                      </div>

                      {results ? null : (
                        <p className="mt-2 text-[13px] leading-[1.5] text-[#315572]">
                          Enter your unit cost, freight and quantity to see the
                          landed cost.
                        </p>
                      )}

                      <div className="mt-6 space-y-4 border-t border-[#17324d]/15 pt-6 text-sm">
                        <div className="flex items-center justify-between gap-4">
                          <span>Total Landed Cost</span>
                          <span className="font-bold">
                            {results
                              ? formatCurrency(results.totalLandedCost)
                              : EMPTY_VALUE}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span>Total Duties</span>
                          <span className="font-bold">
                            {results
                              ? formatCurrency(results.dutyAmount)
                              : EMPTY_VALUE}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span>Freight per Unit</span>
                          <span className="font-bold">
                            {results
                              ? formatCurrency(results.freightPerUnit)
                              : EMPTY_VALUE}
                          </span>
                        </div>
                      </div>

                      <Link
                        href={startFreePath}
                        className="mt-8 inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#17324d] px-4 text-sm font-bold text-white transition hover:bg-[#234969]"
                      >
                        Track this cost automatically
                      </Link>

                      {/*
                       * A greyed-out button with no explanation reads as
                       * broken rather than as waiting. It needs figures to
                       * export, so it says that instead of leaving the visitor
                       * clicking a dead control to find out.
                       */}
                      <button
                        type="button"
                        onClick={downloadReport}
                        disabled={!results}
                        title={
                          results
                            ? undefined
                            : 'Enter your unit cost, freight and quantity first'
                        }
                        className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#17324d]/35 text-sm font-semibold text-[#17324d] transition hover:bg-white/70 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
                      >
                        <Download className="h-4 w-4" />
                        Export as CSV
                      </button>

                      {!results ? (
                        <p className="mt-2 text-center text-xs text-[#17324d]/70">
                          Fill in the figures to export
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="border-border mt-8 flex flex-col gap-5 border-t pt-8 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                      <Network className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-foreground text-base font-bold">
                        One shipment is a calculation. Fifty is a spreadsheet
                        problem.
                      </p>
                      <p className="text-muted-foreground mt-1 max-w-xl text-sm leading-6">
                        {siteName} keeps the real landed cost of every purchase
                        order up to date as supplier costs, freight invoices,
                        and duty land, so you are not rebuilding this by hand
                        each time.
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    className="h-11 shrink-0 rounded-xl px-6 font-bold"
                  >
                    <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section
          id="how-it-works"
          className="border-border/80 bg-card border-y px-4 py-20"
        >
          <div className="mx-auto max-w-4xl">
            <h2 className="text-foreground text-3xl font-extrabold">
              What landed cost actually is
            </h2>
            <p className="text-muted-foreground mt-6 text-lg leading-8">
              Landed cost is every inbound charge that makes one unit sellable,
              divided by the units you can sell — not the factory price, and not
              the PO quantity if some of the lot is unsellable. The calculator
              above is the three-line version you can run before a PO: product,
              freight, duty. The full method, including Incoterms and
              sellable-unit math, is in{' '}
              <Link
                href="/blog/how-to-calculate-landed-cost"
                className="text-primary font-semibold hover:underline"
              >
                how to calculate landed cost
              </Link>
              .
            </p>

            <div className="border-border bg-muted mt-10 rounded-2xl border p-8 text-center">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The formula this page uses
              </p>
              <p className="text-foreground mt-4 font-mono text-lg font-bold md:text-xl">
                (product × qty + freight + duty on product) ÷ qty
              </p>
              <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-sm leading-7">
                Example: 500 × $12 = $6,000 product. 7.5% duty = $450. $840
                freight. Total $7,290 ÷ 500 = <strong>$14.58 per unit</strong>.
                Freight is $1.68 each. If you only shipped 100 units, that same
                $840 freight becomes $8.40 each.
              </p>
            </div>

            <div className="mt-14">
              <h3 className="text-foreground text-2xl font-bold">
                Why calculating it accurately matters
              </h3>

              <div className="mt-8 grid gap-6 md:grid-cols-2">
                {impactCards.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div key={item.title} className="flex gap-4">
                      <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="text-foreground font-bold">
                          {item.title}
                        </h4>
                        <p className="text-muted-foreground mt-1 text-sm leading-6">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-foreground text-center text-3xl font-extrabold">
              Landed cost calculator FAQ
            </h2>

            <div className="mt-12">
              <Accordion
                type="single"
                collapsible
                defaultValue="how-to-calculate"
              >
                {faqItems.map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border-border bg-card mb-4 overflow-hidden rounded-2xl border px-6"
                  >
                    <AccordionTrigger className="text-foreground py-6 text-left text-base font-bold hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section
          id="resources"
          className="relative overflow-hidden bg-[#132238] py-24 text-white sm:py-32"
        >
          <div className="absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full bg-sky-400/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculation is free. Keeping it true is the work.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Your landed cost changes after you order.
              </h2>
              <p className="mt-6 text-lg leading-8 text-white/70">
                Freight gets rebilled, duty differs from the estimate, and the
                supplier revises the invoice. {siteName} keeps purchase orders,
                suppliers, documents, freight, and duty in one workspace, so the
                cost per unit above stays accurate through the whole order
                instead of going stale the day you export it.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="h-12 rounded-xl bg-white px-7 font-bold text-[#132238] hover:bg-white/90"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 rounded-xl border-white/30 bg-transparent px-7 font-bold text-white hover:bg-white/10 hover:text-white"
                >
                  <Link href="/contact">Talk to us</Link>
                </Button>
              </div>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/65">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {siteName} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{siteName}</p>
                      <p className="text-muted-foreground text-xs">
                        Example order overview
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    On track
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        Purchase order SA-1048
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $55.28
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Landed cost per unit, updated
                      </p>
                    </div>
                    <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      100 units
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[82%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Order placed</span>
                    <span>Delivered</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <Package className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$50.00</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Supplier unit price
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Ship className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$2.53</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Freight per unit, rebilled
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <FileText className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$2.75</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Duty per unit
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle2 className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Purchasing, operations, and finance all see the same
                      number.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl">
            <h2 className="text-foreground text-2xl font-bold">
              Related reading
            </h2>
            <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
              <li>
                <Link
                  href="/blog/how-to-calculate-landed-cost"
                  className="text-primary font-semibold hover:underline"
                >
                  How to calculate landed cost
                </Link>{' '}
                — inbound formula, sellable units, and a worked shipment.
              </li>
              <li>
                <Link
                  href="/blog/how-to-calculate-landed-cost-for-imported-products"
                  className="text-primary font-semibold hover:underline"
                >
                  Landed cost for imported products
                </Link>{' '}
                — the importer-policy version of the same math.
              </li>
              <li>
                <Link
                  href="/blog/calculate-import-duty-from-china"
                  className="text-primary font-semibold hover:underline"
                >
                  Import duty from China
                </Link>{' '}
                — HS/HTS, Section 301, and MPF/HMF before you plug a rate in
                above.
              </li>
              <li>
                <Link
                  href="/blog/landed-cost-per-unit"
                  className="text-primary font-semibold hover:underline"
                >
                  Landed cost per unit
                </Link>{' '}
                — allocating freight and fees across SKUs.
              </li>
            </ul>
          </div>
          <RelatedTools currentPath="/tools/landed-cost-calculator" />
        </section>
      </div>
    </>
  );
}
